/** @jsx jsx */
import React, { forwardRef } from 'react';
import { styled, css, jsx } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { CellWrapperProps } from '@atlassian/jira-business-fields/cell-wrapper';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';

export const SHADOW_SIZE = 2;
export const SELECTED_CELL_BOX_SHADOW = `0 0 0 ${SHADOW_SIZE}px ${token('color.border.focused', colors.B200)} inset`;
export const DROPDOWN_WIDTH = 160;
export const DROPDOWN_HEIGHT = 40;

/**
 * To be deleted when jira_list_keyboard_nav is cleaned up
 * @deprecated Use WrapperCommon from @atlassian/jira-business-fields/cell-wrapper instead
 */
// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const WrapperCommon = styled.div<{ isFocused?: boolean }>({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: ({ isFocused }) => (isFocused ? SELECTED_CELL_BOX_SHADOW : 'none'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isFocused }) =>
		isFocused ? token('elevation.surface', 'white') : 'transparent',
	outline: 'none',
});

/**
 * To be deleted when jira_list_keyboard_nav is cleaned up
 * @deprecated Use CellWrapper from @atlassian/jira-business-fields/cell-wrapper instead
 */
// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CellWrapper = styled(WrapperCommon)<CellWrapperProps>({
	boxSizing: 'border-box',
	borderRadius: token('border.radius', '3px'),
	paddingLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isEditable = true }) => (isEditable ? 'pointer' : 'default'),
});

type CellContentContainerProps = {
	width: number;
	height: number;
};

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CellContentContainer = styled.div<CellContentContainerProps>({
	outline: 'none',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	width: `${({ width }: { width: number }) => width - (gridSize * 3 + SHADOW_SIZE * 2)}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${({ height }: { height: number }) => height - SHADOW_SIZE * 2}px`,
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CellContainer = styled.div<{ isFocused: boolean }>({
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		display: 'flex',
		alignItems: 'center',
		width: '100%,',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		boxShadow: ({ isFocused }) => (isFocused ? SELECTED_CELL_BOX_SHADOW : 'none'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ isFocused }) =>
			isFocused ? token('elevation.surface', 'white') : 'transparent',
		outline: 'none',
		borderRadius: `${token('border.radius', '4px')}`,
	},
	outline: 'none',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CellContentWrapper = styled.div<{ preferredWidth: string | number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${({ preferredWidth }: { preferredWidth: string | number }) => preferredWidth}px`,
	minWidth: '280px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> div > div:first-of-type': {
		borderWidth: 0,
		boxShadow: SELECTED_CELL_BOX_SHADOW,
	},
});

/**
 * To be deleted when jira_list_keyboard_nav is cleaned up
 * @deprecated Use TextCellWrapper from @atlassian/jira-business-fields/cell-wrapper instead
 */
// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const TextFieldWrapper = styled.div<{
	isEditable?: boolean;
	isSelected?: boolean;
	isFocused?: boolean;
}>({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: { isFocused?: boolean }) =>
		props.isFocused ? token('elevation.surface', 'white') : 'transparent',
	outline: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isEditable = true }) => (isEditable ? 'text' : 'default'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> input': {
			height: '100%',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			paddingLeft: token('space.025', '2px'),
			borderWidth: 0,
			boxShadow: SELECTED_CELL_BOX_SHADOW,
			height: '100%',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		padding: `0 ${token('space.075', '6px')}`,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: (props: { isSelected?: boolean; isFocused?: boolean }) =>
		props.isSelected || props.isFocused ? SELECTED_CELL_BOX_SHADOW : 'none',
	boxSizing: 'border-box',
	borderRadius: `${token('border.radius', '4px')}`,
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AvatarItem = styled.div<{ isUnassigned?: boolean }>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props: any) => (props.isUnassigned ? 0 : 1),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props: any) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		props.isUnassigned ? token('color.text.subtlest', colors.N100) : 'inherit',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100% - ${gridSize / 2}px)`,
	'&:hover': {
		opacity: 1,
	},
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DisplayName = styled.span({
	marginLeft: token('space.050', '4px'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmptyCellContent = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N100),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		paddingRight: token('space.100', '8px'),
	},
	pointerEvents: 'none',
});

const containerBaseStyles = css({
	display: 'flex',
	alignItems: 'center',
	height: '100%',
	borderRadius: token('border.radius.050', '4px'),
	cursor: 'pointer',
	backgroundColor: 'transparent',
	paddingLeft: token('space.100', '8px'),
	boxShadow: 'none',
});

const containerMenuOpenStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	paddingLeft: token('space.0', '0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	boxShadow: `0 0 0 ${token('space.025', 'px')} ${token('color.border.focused', colors.B200)} inset`,
});

export interface ContainerProps {
	width?: string | number;
	isMenuOpen?: boolean;
	children: React.ReactNode;
}

export const Container = forwardRef<HTMLDivElement, ContainerProps>(
	({ width, isMenuOpen, children, ...rest }, ref) => (
		<div
			css={[containerBaseStyles, isMenuOpen && containerMenuOpenStyles]}
			ref={ref}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ width: width ? `${width}px` : '100%' }}
			{...rest}
		>
			{children}
		</div>
	),
);
